<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ContactDetailsItem from 'dashboard/routes/dashboard/conversation/ContactDetailsItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    MultiselectDropdown,
    ContactDetailsItem,
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['cancel', 'update:show'],
  setup() {
    const { agentsList } = useAgentsList(true, { includeAgentBots: true });
    return {
      agentsList,
    };
  },
  data() {
    return {
      selectedAgent: null,
      selectedTeam: null,
      transferMessage: '',
      isAgentDirty: false,
      isTeamDirty: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        if (this.isAgentDirty) return this.selectedAgent;
        const assignee = this.currentChat.meta.assignee;
        return (
          assignee && {
            ...assignee,
            assignee_type: this.currentChat.meta.assignee_type || 'User',
          }
        );
      },
      set(agent) {
        this.selectedAgent = agent;
        this.isAgentDirty = true;
      },
    },
    assignedTeam: {
      get() {
        if (this.isTeamDirty) return this.selectedTeam;
        return this.currentChat.meta.team;
      },
      set(team) {
        this.selectedTeam = team;
        this.isTeamDirty = true;
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (
        this.assignedAgent.id !== this.currentUser.id ||
        (this.assignedAgent.assignee_type || 'User') !== 'User'
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    onCancel() {
      this.selectedAgent = null;
      this.selectedTeam = null;
      this.transferMessage = '';
      this.isAgentDirty = false;
      this.isTeamDirty = false;
      this.$emit('cancel');
    },
    async onSubmit() {
      if (this.isAgentDirty) {
        const agentId = this.selectedAgent ? this.selectedAgent.id : null;
        const assigneeType = this.selectedAgent ? this.selectedAgent.assignee_type || 'User' : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: this.selectedAgent,
          assigneeType,
        });
        await this.$store.dispatch('assignAgent', {
          conversationId: this.currentChat.id,
          agentId,
          assigneeType,
        });
      }
      
      if (this.isTeamDirty) {
        const teamId = this.selectedTeam ? this.selectedTeam.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team: this.selectedTeam, conversationId: this.currentChat.id });
        await this.$store.dispatch('assignTeam', { conversationId: this.currentChat.id, teamId });
      }

      if (this.transferMessage.trim() !== '') {
        await this.$store.dispatch('createPendingMessageAndSend', {
          conversation_id: this.currentChat.id,
          content: this.transferMessage,
          private: true,
        });
      }

      if (this.isAgentDirty || this.isTeamDirty) {
        useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
      }
      
      this.onCancel();
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (
        this.assignedAgent?.id === selectedItem.id &&
        (this.assignedAgent?.assignee_type || 'User') ===
          (selectedItem.assignee_type || 'User')
      ) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },
    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        header-title="Transferir Atendimento"
        header-content="Escolha um atendente ou uma fila para transferir esta conversa."
      />
      <div class="w-full px-8 pb-8 flex flex-col gap-4">
        <div>
          <ContactDetailsItem
            compact
            :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
          >
            <template #button>
              <NextButton
                v-if="showSelfAssign"
                link
                xs
                icon="i-lucide-arrow-right"
                class="!gap-1"
                :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
                @click="onSelfAssign"
              />
            </template>
          </ContactDetailsItem>
          <MultiselectDropdown
            :options="agentsList"
            :selected-item="assignedAgent"
            :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
            :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
            :no-search-result="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
            "
            :input-placeholder="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
            "
            @select="onClickAssignAgent"
          />
        </div>
        <div>
          <ContactDetailsItem
            compact
            :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
          />
          <MultiselectDropdown
            :options="teamsList"
            :selected-item="assignedTeam"
            show-emoji-icon
            :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
            :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
            :no-search-result="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
            "
            :input-placeholder="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
            "
            @select="onClickAssignTeam"
          />
        </div>
        <div>
          <ContactDetailsItem
            compact
            title="Nota Interna (Oculto p/ o Cliente)"
          />
          <textarea
            v-model="transferMessage"
            class="w-full mt-2 p-3 text-sm bg-n-surface-2 border border-n-weak rounded-md focus:border-n-brand focus:ring-1 focus:ring-n-brand outline-none transition-colors resize-none min-h-[80px]"
            placeholder="Deixe uma mensagem para o próximo atendente que irá receber este ticket..."
          ></textarea>
        </div>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-8 py-4 bg-n-background border-t border-n-weak">
        <NextButton
          faded
          slate
          :label="$t('EMAIL_TRANSCRIPT.CANCEL')"
          @click.prevent="onCancel"
        />
        <NextButton
          :label="$t('EMAIL_TRANSCRIPT.SUBMIT')"
          @click.prevent="onSubmit"
        />
      </div>
    </div>
  </woot-modal>
</template>
